import { Search, X } from 'lucide-react';
import { hasActiveContactsFilters, SORT_OPTIONS } from '../../hooks/contacts-search-core';
import type { ContactSortBy, ContactSortDir, ContactsFilters } from '../../hooks/useContactsSearch';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export interface ContactsFilterBarProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  filters: ContactsFilters;
  onSetFilter: <K extends keyof ContactsFilters>(key: K, value: ContactsFilters[K]) => void;
  onResetFilters: () => void;
}

export function ContactsFilterBar({
  searchInput,
  onSearchInputChange,
  filters,
  onSetFilter,
  onResetFilters,
}: ContactsFilterBarProps) {
  const { data: contactTypesData } = trpc.contactTypes.list.useQuery();
  const contactTypes = contactTypesData?.items || [];
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();

  const hasActiveFilters = hasActiveContactsFilters(searchInput, filters);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            className="pl-10 bg-card"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
          />
        </div>

        <Select
          value={filters.typeId ? String(filters.typeId) : 'all'}
          onValueChange={(val) => onSetFilter('typeId', val === 'all' ? undefined : Number(val))}
        >
          <SelectTrigger className="w-full md:w-[180px] bg-card">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {contactTypes.map((type) => (
              <SelectItem key={type.id} value={String(type.id)}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.ownerId ? String(filters.ownerId) : 'all'}
          onValueChange={(val) => onSetFilter('ownerId', val === 'all' ? undefined : Number(val))}
        >
          <SelectTrigger className="w-full md:w-[180px] bg-card">
            <SelectValue placeholder="All owners" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {members.map((member) => (
              <SelectItem key={member.id} value={String(member.id)}>
                {member.fullName || member.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Company..."
          className="w-full md:w-[180px] bg-card"
          value={filters.companyName ?? ''}
          onChange={(e) => onSetFilter('companyName', e.target.value || undefined)}
        />

        <Select
          value={`${filters.sortBy}:${filters.sortDir}`}
          onValueChange={(val) => {
            const [sortBy, sortDir] = val.split(':') as [ContactSortBy, ContactSortDir];
            onSetFilter('sortBy', sortBy);
            onSetFilter('sortDir', sortDir);
          }}
        >
          <SelectTrigger className="w-full md:w-[180px] bg-card">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={filters.hasEmail === true}
            onCheckedChange={(checked) => onSetFilter('hasEmail', checked ? true : undefined)}
          />
          Has email
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={filters.hasPhone === true}
            onCheckedChange={(checked) => onSetFilter('hasPhone', checked ? true : undefined)}
          />
          Has phone
        </label>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-muted-foreground"
            onClick={onResetFilters}
          >
            <X className="h-3 w-3" /> Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
